import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, X, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CascadingCategoryPicker } from "@/components/catalog/CascadingCategoryPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const STORAGE_BUCKET = "line-item-images";
const STORAGE_PREFIX = "system-line-items/";
const DOYENCE_VAT_RATE = 15;
const STALE_DAYS = 90;
const isStale = (iso: string | null): boolean => {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > STALE_DAYS * 24 * 60 * 60 * 1000;
};

interface BrowseCat {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
}
interface Classif { id: string; name: string }
interface Trade { id: string; name: string }
interface Unit { id: string; name: string; symbol: string | null }

interface PlatformSupplier {
  id: string;
  name: string;
  website: string | null;
}

interface PriceSource {
  id: string | null;
  sort_order: 1 | 2 | 3;
  platform_supplier_id: string | null;
  source_name: string;
  url: string;
  last_seen_price: string;
  last_checked_at: string | null;
  is_dirty: boolean;
}

interface SystemItem {
  id: string;
  name: string;
  description: string | null;
  notes: string | null;
  browse_category_id: string | null;
  category_id: string | null;
  trade_id: string | null;
  unit_id: string | null;
  base_cost: number;
  default_markup_percent: number;
  image_urls: string[];
  is_active: boolean;
  price_includes_vat: boolean;
}

export default function SystemLineItemForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const adminId = session?.user?.id ?? null;

  const [form, setForm] = useState({
    name: "",
    description: "",
    notes: "",
    browse_category_id: "__none__",
    category_id: "",
    trade_id: "__none__",
    unit_id: "__none__",
    base_cost: "0",
    default_markup_percent: "0",
    is_active: true,
    price_includes_vat: false,
    image_urls: [] as string[],
  });
  const [editing, setEditing] = useState<SystemItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sources, setSources] = useState<PriceSource[]>([]);
  const [savingSources, setSavingSources] = useState(false);
  const [openSlot, setOpenSlot] = useState<1 | 2 | 3 | null>(null);
  const [sourceCheckedInSession, setSourceCheckedInSession] = useState(false);
  const [markingCurrent, setMarkingCurrent] = useState(false);

  const browseQuery = useQuery({
    queryKey: ["sli-browse-cats"],
    queryFn: async (): Promise<BrowseCat[]> => {
      const { data, error } = await supabase
        .from("browse_categories")
        .select("id, name, parent_id, depth, display_order")
        .is("company_id", null)
        .order("depth", { ascending: true })
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BrowseCat[];
    },
  });

  const classifQuery = useQuery({
    queryKey: ["slif-classif"],
    queryFn: async (): Promise<Classif[]> => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("id, name")
        .eq("is_default", true)
        .order("name");
      if (error) throw error;
      return (data ?? []) as Classif[];
    },
  });

  const tradesQuery = useQuery({
    queryKey: ["slif-trades"],
    queryFn: async (): Promise<Trade[]> => {
      const { data, error } = await supabase
        .from("supplier_trades")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Trade[];
    },
  });

  const unitsQuery = useQuery({
    queryKey: ["slif-units"],
    queryFn: async (): Promise<Unit[]> => {
      const { data, error } = await supabase
        .from("line_item_units")
        .select("id, name, symbol")
        .is("company_id", null)
        .order("name");
      if (error) throw error;
      return (data ?? []) as Unit[];
    },
  });

  const platformSuppliersQuery = useQuery({
    queryKey: ["slif-platform-suppliers"],
    queryFn: async (): Promise<PlatformSupplier[]> => {
      const { data, error } = await supabase
        .from("platform_suppliers")
        .select("id, name, website")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return (data ?? []) as PlatformSupplier[];
    },
  });

  const sourcesQuery = useQuery({
    queryKey: ["slif-sources", id],
    enabled: isEdit,
    queryFn: async (): Promise<PriceSource[]> => {
      const { data, error } = await supabase
        .from("system_line_item_price_sources")
        .select("*")
        .eq("system_line_item_id", id!)
        .order("sort_order");
      if (error) throw error;
      return ((data ?? []) as any[]).map((r) => ({
        id: r.id,
        sort_order: r.sort_order,
        platform_supplier_id: r.platform_supplier_id ?? null,
        source_name: r.source_name ?? "",
        url: r.url ?? "",
        last_seen_price: r.last_seen_price != null ? String(r.last_seen_price) : "",
        last_checked_at: r.last_checked_at ?? null,
        is_dirty: false,
      }));
    },
  });

  const itemQuery = useQuery({
    queryKey: ["system-line-item", id],
    enabled: isEdit,
    queryFn: async (): Promise<SystemItem> => {
      const { data, error } = await supabase
        .from("system_line_items")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as SystemItem;
    },
  });

  useEffect(() => {
    const it = itemQuery.data;
    if (!it) return;
    setEditing(it);
    setForm({
      name: it.name,
      description: it.description ?? "",
      notes: it.notes ?? "",
      browse_category_id: it.browse_category_id ?? "__none__",
      category_id: it.category_id ?? "",
      trade_id: it.trade_id ?? "__none__",
      unit_id: it.unit_id ?? "__none__",
      base_cost: String(it.base_cost),
      default_markup_percent: String(it.default_markup_percent),
      is_active: it.is_active,
      price_includes_vat: it.price_includes_vat ?? false,
      image_urls: [...(it.image_urls ?? [])],
    });
  }, [itemQuery.data]);

  useEffect(() => {
    if (sourcesQuery.data) {
      setSources(sourcesQuery.data);
    }
  }, [sourcesQuery.data]);

  const browseCats = browseQuery.data ?? [];
  const classifs = classifQuery.data ?? [];
  const trades = tradesQuery.data ?? [];
  const units = unitsQuery.data ?? [];

  const vatPreview = useMemo(() => {
    const cost = Number(form.base_cost);
    if (Number.isNaN(cost) || cost <= 0) return null;
    if (form.price_includes_vat) {
      const exVat = cost / (1 + DOYENCE_VAT_RATE / 100);
      return { exVat: exVat.toFixed(2), incVat: cost.toFixed(2) };
    } else {
      const incVat = cost * (1 + DOYENCE_VAT_RATE / 100);
      return { exVat: cost.toFixed(2), incVat: incVat.toFixed(2) };
    }
  }, [form.base_cost, form.price_includes_vat]);

  const writeAudit = async (
    action: "system_line_item_created" | "system_line_item_updated",
    entityId: string,
    details: Record<string, unknown>
  ) => {
    if (!adminId) return;
    await supabase.from("admin_audit_logs").insert({
      admin_id: adminId,
      action,
      entity_type: "system_line_item",
      entity_id: entityId,
      details: details as never,
    });
  };

  const extractStoragePathFromUrl = (url: string): string | null => {
    const marker = `/object/public/${STORAGE_BUCKET}/`;
    const idx = url.indexOf(marker);
    if (idx < 0) return null;
    return url.slice(idx + marker.length);
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_IMAGES - form.image_urls.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setUploading(true);
    try {
      const newUrls: string[] = [];
      for (const file of list) {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          toast.error(`${file.name}: unsupported file type`);
          continue;
        }
        if (file.size > MAX_IMAGE_SIZE) {
          toast.error(`${file.name}: exceeds 5MB`);
          continue;
        }
        const ext = file.name.split(".").pop() || "jpg";
        const path = `${STORAGE_PREFIX}system-item-${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
        const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
          contentType: file.type,
          upsert: false,
        });
        if (error) {
          toast.error(`${file.name}: ${error.message}`);
          continue;
        }
        const { data: pub } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
        newUrls.push(pub.publicUrl);
      }
      if (newUrls.length > 0) {
        setForm((f) => ({ ...f, image_urls: [...f.image_urls, ...newUrls] }));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = async (url: string) => {
    const path = extractStoragePathFromUrl(url);
    if (path) {
      await supabase.storage.from(STORAGE_BUCKET).remove([path]);
    }
    setForm((f) => ({ ...f, image_urls: f.image_urls.filter((u) => u !== url) }));
  };

  const updateSource = (sortOrder: 1 | 2 | 3, patch: Partial<PriceSource>) => {
    setSources((prev) => {
      const existing = prev.find((s) => s.sort_order === sortOrder);
      if (existing) {
        return prev.map((s) =>
          s.sort_order === sortOrder ? { ...s, ...patch, is_dirty: true } : s
        );
      } else {
        const newSource: PriceSource = {
          id: null,
          sort_order: sortOrder,
          platform_supplier_id: null,
          source_name: "",
          url: "",
          last_seen_price: "",
          last_checked_at: null,
          is_dirty: true,
          ...patch,
        };
        return [...prev, newSource];
      }
    });
  };

  const markChecked = async (source: PriceSource) => {
    const now = new Date().toISOString();
    if (source.id) {
      await (supabase as any)
        .from('system_line_item_price_sources')
        .update({ last_checked_at: now, updated_by: adminId })
        .eq('id', source.id);
      queryClient.invalidateQueries({ queryKey: ['slif-sources', id] });
      setSourceCheckedInSession(true);
    }
    setSources((prev) =>
      prev.map((s) =>
        s.sort_order === source.sort_order ? { ...s, last_checked_at: now } : s
      )
    );
  };

  const markPriceAsCurrent = async () => {
    if (!id) return;
    setMarkingCurrent(true);
    try {
      const { error } = await supabase
        .from('system_line_items')
        .update({
          price_last_updated_at: new Date().toISOString(),
          updated_by: adminId,
        })
        .eq('id', id)
        .select();
      if (error) throw error;
      toast.success('Price marked as current');
      queryClient.invalidateQueries({ queryKey: ['system-line-item', id] });
      queryClient.invalidateQueries({ queryKey: ['system-line-items'] });
      setSourceCheckedInSession(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark as current');
    } finally {
      setMarkingCurrent(false);
    }
  };

  const saveSources = async (systemLineItemId: string) => {
    setSavingSources(true);
    let savedCount = 0;
    let skippedCount = 0;
    try {
      for (const source of sources) {
        const hasContent =
          source.platform_supplier_id ||
          source.source_name.trim() ||
          source.url.trim() ||
          source.last_seen_price.trim();

        if (!hasContent) {
          if (source.id) {
            const { error } = await (supabase as any)
              .from('system_line_item_price_sources')
              .delete()
              .eq('id', source.id);
            if (error) {
              console.error('Delete error:', error);
              toast.error(`Failed to remove Source ${source.sort_order}: ${error.message}`);
            }
          }
          continue;
        }

        if (!source.platform_supplier_id && !source.source_name.trim()) {
          toast.error(`Source ${source.sort_order}: select a supplier or enter a source name`);
          skippedCount++;
          continue;
        }

        const urlVal = source.url.trim();
        if (urlVal && !/^https?:\/\//i.test(urlVal)) {
          toast.error(`Source ${source.sort_order}: URL must start with http:// or https://`);
          skippedCount++;
          continue;
        }

        const payload = {
          system_line_item_id: systemLineItemId,
          sort_order: source.sort_order,
          platform_supplier_id: source.platform_supplier_id || null,
          source_name: source.source_name.trim() || null,
          url: urlVal || null,
          last_seen_price: source.last_seen_price.trim()
            ? Number(source.last_seen_price)
            : null,
          updated_by: adminId,
        };

        if (source.id) {
          const { error } = await (supabase as any)
            .from('system_line_item_price_sources')
            .update(payload)
            .eq('id', source.id);
          if (error) {
            console.error('Update error:', error);
            toast.error(`Source ${source.sort_order}: ${error.message}`);
            skippedCount++;
            continue;
          }
        } else {
          const { data: inserted, error } = await (supabase as any)
            .from('system_line_item_price_sources')
            .insert({ ...payload, created_by: adminId })
            .select()
            .single();
          if (error) {
            console.error('Insert error:', error);
            toast.error(`Source ${source.sort_order}: ${error.message}`);
            skippedCount++;
            continue;
          }
          if (inserted) {
            setSources(prev => prev.map(s =>
              s.sort_order === source.sort_order ? { ...s, id: inserted.id, is_dirty: false } : s
            ));
            savedCount++;
            continue;
          }
        }
        savedCount++;
      }

      if (savedCount > 0) {
        toast.success(`${savedCount} source${savedCount > 1 ? 's' : ''} saved`);
      } else if (skippedCount === 0) {
        toast.info('No changes to save');
      }

      await queryClient.refetchQueries({ queryKey: ['slif-sources', id] });
      setSources(prev => prev.map(s => ({ ...s, is_dirty: false })));
    } catch (err) {
      console.error('saveSources error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save sources');
    } finally {
      setSavingSources(false);
    }
  };

  const goBack = () => navigate("/catalog/system-items");


  const handleSubmit = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) { toast.error("Name is required"); return; }
    if (trimmedName.length > 200) { toast.error("Name must be 200 characters or fewer"); return; }
    if (!form.category_id) { toast.error("Classification category is required"); return; }
    const baseCost = Number(form.base_cost);
    if (Number.isNaN(baseCost) || baseCost < 0) { toast.error("Base cost must be 0 or greater"); return; }
    const markup = Number(form.default_markup_percent) || 0;
    if (markup < 0 || markup > 100) { toast.error("Markup must be between 0 and 100"); return; }

    setSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
        browse_category_id: form.browse_category_id === "__none__" ? null : form.browse_category_id,
        category_id: form.category_id,
        trade_id: form.trade_id === "__none__" ? null : form.trade_id,
        unit_id: form.unit_id === "__none__" ? null : form.unit_id,
        base_cost: baseCost,
        default_markup_percent: markup,
        image_urls: form.image_urls,
        is_active: form.is_active,
        price_includes_vat: form.price_includes_vat,
      };

      if (isEdit && editing) {
        const baseCostChanged = Number(editing.base_cost) !== baseCost;
        const update = {
          ...payload,
          updated_by: adminId,
          ...(baseCostChanged ? { price_last_updated_at: new Date().toISOString() } : {}),
        };
        const { error } = await supabase
          .from("system_line_items")
          .update(update)
          .eq("id", editing.id)
          .select();
        if (error) throw error;

        const changes: Record<string, { old: unknown; new: unknown }> = {};
        const compare: Array<[keyof typeof payload, unknown, unknown]> = [
          ["name", editing.name, payload.name],
          ["description", editing.description, payload.description],
          ["notes", editing.notes, payload.notes],
          ["browse_category_id", editing.browse_category_id, payload.browse_category_id],
          ["category_id", editing.category_id, payload.category_id],
          ["trade_id", editing.trade_id, payload.trade_id],
          ["unit_id", editing.unit_id, payload.unit_id],
          ["base_cost", Number(editing.base_cost), payload.base_cost],
          ["default_markup_percent", Number(editing.default_markup_percent), payload.default_markup_percent],
          ["is_active", editing.is_active, payload.is_active],
          ["price_includes_vat", editing.price_includes_vat, payload.price_includes_vat],
        ];
        compare.forEach(([k, oldV, newV]) => {
          if (oldV !== newV) changes[k as string] = { old: oldV, new: newV };
        });
        const oldImgs = JSON.stringify(editing.image_urls ?? []);
        const newImgs = JSON.stringify(payload.image_urls);
        if (oldImgs !== newImgs) changes.image_urls = { old: editing.image_urls, new: payload.image_urls };

        await writeAudit("system_line_item_updated", editing.id, {
          name: trimmedName,
          changes,
        });
        toast.success("Item updated");
      } else {
        const { data, error } = await supabase
          .from("system_line_items")
          .insert({
            ...payload,
            created_by: adminId,
            updated_by: adminId,
            price_last_updated_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (error) throw error;
        await writeAudit("system_line_item_created", data.id, {
          name: trimmedName,
          category_id: payload.category_id,
          browse_category_id: payload.browse_category_id,
          base_cost: baseCost,
          default_markup_percent: markup,
        });
        toast.success("Item created");
      }
      queryClient.invalidateQueries({ queryKey: ["system-line-items"] });
      navigate("/catalog/system-items");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const loading = isEdit && itemQuery.isLoading;

  return (
    <div className="space-y-4 max-w-3xl bg-slate-950">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          onClick={goBack}
          className="text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to System Line Items
        </Button>
      </div>

      {loading ? (
        <div className="bg-slate-900 border border-slate-800 rounded-md p-10 flex items-center justify-center text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-md p-6 space-y-6">
          {/* Item details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white">Item Details</h3>
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">Name *</Label>
              <Input
                id="name"
                maxLength={200}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-300">Description</Label>
              <Textarea
                id="description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-slate-300">Notes</Label>
              <Textarea
                id="notes"
                rows={2}
                value={form.notes}
                placeholder="e.g. Price as at [month year]"
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <div className="border-t border-slate-800" />

          {/* Classification & pricing */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white">Classification & Pricing</h3>

            <div className="space-y-2">
              <Label className="text-slate-300">Browse Category</Label>
              <CascadingCategoryPicker
                categories={browseCats}
                value={form.browse_category_id === "__none__" ? null : form.browse_category_id}
                onChange={(v) =>
                  setForm((p) => ({ ...p, browse_category_id: v ?? "__none__" }))
                }
                placeholder="Select browse category"
                className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Classification Category *</Label>
              <Select
                value={form.category_id}
                onValueChange={(v) => setForm((p) => ({ ...p, category_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {classifs.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Controls which markup tier applies when companies use this item.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Trade</Label>
              <Select value={form.trade_id} onValueChange={(v) => setForm((p) => ({ ...p, trade_id: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select trade (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {trades.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Unit</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm((p) => ({ ...p, unit_id: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}{u.symbol ? ` (${u.symbol})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="base_cost" className="text-slate-300">Base Cost *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R</span>
                <Input
                  id="base_cost"
                  type="number"
                  min={0}
                  step="0.01"
                  className="pl-7 bg-slate-800 border-slate-700 text-white"
                  value={form.base_cost}
                  onChange={(e) => setForm((p) => ({ ...p, base_cost: e.target.value }))}
                />
              </div>
              <p className="text-xs text-slate-400">Enter your cost before markup. Companies apply their own markup tiers on top.</p>
            </div>

            {/* Mark price as current — edit mode only */}
            {isEdit && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={markPriceAsCurrent}
                  disabled={markingCurrent}
                  className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  {markingCurrent ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Mark price as current
                </Button>
                <p className="text-xs text-slate-400">
                  Use this after verifying the base cost is still accurate. Updates the "Price Updated" timestamp on the item list.
                </p>
                {sourceCheckedInSession && (
                  <p className="text-xs text-amber-400">
                    You have verified price sources this session. If the base cost has changed, update it above before saving. If it has not changed, mark the price as current.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="price_includes_vat" className="text-slate-300">Price includes VAT</Label>
                <p className="text-xs text-slate-400">Toggle on if the price you entered already includes {DOYENCE_VAT_RATE}% VAT.</p>
              </div>
              <Switch
                id="price_includes_vat"
                checked={form.price_includes_vat}
                onCheckedChange={(c) => setForm((p) => ({ ...p, price_includes_vat: c }))}
              />
            </div>

            {vatPreview && (
              <div className="flex items-center gap-4 text-sm">
                <div className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex-1">
                  <span className="text-slate-400 block text-xs">Ex VAT</span>
                  <span className="text-white font-medium">R {vatPreview.exVat}</span>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex-1">
                  <span className="text-slate-400 block text-xs">Inc VAT ({DOYENCE_VAT_RATE}%)</span>
                  <span className="text-white font-medium">R {vatPreview.incVat}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="markup" className="text-slate-300">Default Markup %</Label>
              <Input
                id="markup"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.default_markup_percent}
                onChange={(e) => setForm((p) => ({ ...p, default_markup_percent: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-400">Fallback only — used when a company has no markup tier configured for this item's classification category.</p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-slate-300">Active</Label>
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(c) => setForm((p) => ({ ...p, is_active: c }))}
              />
            </div>
          </div>

          <div className="border-t border-slate-800" />

          {/* Images */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white">Images</h3>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(",")}
                multiple
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || form.image_urls.length >= MAX_IMAGES}
                className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 border-dashed"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? "Uploading…" : "Upload Image"}
              </Button>
              <p className="text-xs text-slate-400 mt-1">
                JPG, PNG, or WEBP. Max 5MB each. Up to {MAX_IMAGES} images.
              </p>
            </div>
            {form.image_urls.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.image_urls.map((url) => (
                  <div key={url} className="relative group/img rounded border border-slate-800 overflow-hidden aspect-square bg-slate-950">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(url)}
                      className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover/img:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-800" />

          {/* Price Sources */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Price Sources</h3>
              {isEdit && (
                <Button
                  type="button"
                  onClick={() => saveSources(id!)}
                  disabled={savingSources || !sources.some((s) => s.is_dirty)}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 h-7 text-xs"
                >
                  {savingSources ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                  Save Sources
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">
              Add up to 3 sources where this item's price can be verified. Sources are visible to estimators (name only).
            </p>

            {isEdit ? (
              <div className="space-y-3">
                {([1, 2, 3] as const).map((slot) => {
                  const source = sources.find((s) => s.sort_order === slot);
                  const platformSuppliers = platformSuppliersQuery.data ?? [];
                  const stale = isStale(source?.last_checked_at ?? null);
                  const isOpen = openSlot === slot;

                  const supplierName = source?.platform_supplier_id
                    ? platformSuppliers.find(ps => ps.id === source.platform_supplier_id)?.name
                    : null;
                  const summary = supplierName ?? source?.source_name ?? (source?.url ? source.url.slice(0, 30) + (source.url.length > 30 ? '…' : '') : null);

                  return (
                    <div key={slot} className="rounded-md border border-slate-800 bg-slate-950/50">
                      <button
                        type="button"
                        onClick={() => setOpenSlot(isOpen ? null : slot)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-900/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {isOpen
                            ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                            : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                          }
                          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                            Source {slot}
                          </span>
                        </div>
                        <span className={`text-xs truncate max-w-[200px] ${summary ? 'text-slate-300' : 'text-slate-500 italic'}`}>
                          {summary ?? 'Empty'}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="p-3 pt-0 space-y-3 border-t border-slate-800">
                          <div className="flex items-center justify-end pt-2">
                            {source?.last_checked_at && (
                              <span className={`text-xs ${stale ? 'text-amber-400' : 'text-slate-400'}`}>
                                Last checked: {new Date(source.last_checked_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs">Supplier</Label>
                            <Select
                              value={source?.platform_supplier_id ?? '__none__'}
                              onValueChange={(v) =>
                                updateSource(slot, { platform_supplier_id: v === '__none__' ? null : v })
                              }
                            >
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm">
                                <SelectValue placeholder="Select supplier" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                                <SelectItem value="__none__">— None —</SelectItem>
                                {platformSuppliers.map((ps) => (
                                  <SelectItem key={ps.id} value={ps.id}>{ps.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {!source?.platform_supplier_id && (
                            <div className="space-y-1.5">
                              <Label className="text-slate-300 text-xs">Source Name</Label>
                              <Input
                                value={source?.source_name ?? ''}
                                onChange={(e) => updateSource(slot, { source_name: e.target.value })}
                                className="bg-slate-800 border-slate-700 text-white h-8 text-sm"
                              />
                            </div>
                          )}

                          <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs">Product URL</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                value={source?.url ?? ''}
                                placeholder="https://..."
                                onChange={(e) => updateSource(slot, { url: e.target.value })}
                                className="bg-slate-800 border-slate-700 text-white h-8 text-sm flex-1"
                              />
                              {source?.url && /^https?:\/\//i.test(source.url) && (
                                <a
                                  href={source.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center h-8 w-8 rounded border border-slate-700 bg-slate-800 text-amber-400 hover:text-amber-300 hover:bg-slate-700"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </div>
                          </div>

                          <div className="flex items-end gap-2">
                            <div className="space-y-1.5 flex-1">
                              <Label className="text-slate-300 text-xs">Last Seen Price (R)</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={source?.last_seen_price ?? ''}
                                onChange={(e) => updateSource(slot, { last_seen_price: e.target.value })}
                                className={`bg-slate-800 border-slate-700 text-white h-8 text-sm ${stale && source?.last_checked_at ? 'border-amber-500/50' : ''}`}
                              />
                            </div>
                            {source?.id ? (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => markChecked(source)}
                                className="h-8 text-xs border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white shrink-0"
                              >
                                Mark checked
                              </Button>
                            ) : (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        disabled
                                        className="h-8 text-xs border-slate-700 bg-slate-800 text-slate-500 shrink-0"
                                      >
                                        Mark checked
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>Save sources first to enable mark checked</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-slate-700 bg-slate-950/50 p-4">
                <p className="text-xs text-slate-400 text-center">
                  Price sources can be added after the item is created.
                </p>
              </div>
            )}
          </div>


          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={submitting}
              className="text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Item"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
